import { useState } from 'react';
import {
  Video, Shield, Phone, Brain, Check, ChevronRight, SkipForward, Info
} from 'lucide-react';
import useSiteSetupStore from '../../../stores/siteSetupStore';
import { useBranding } from '../../../contexts/BrandingContext';

const CATALOG = [
  {
    category: 'Video Management',
    Icon: Video,
    color: '#60a5fa',
    items: [
      { type: 'WAVE_VMS',      name: 'Hanwha Wave VMS',              description: 'Connect to your Wave server to discover cameras.' },
      { type: 'MILESTONE_VMS', name: 'Milestone XProtect',           description: 'Connect to an XProtect Management Server.' },
      { type: 'GENETEC_VMS',   name: 'Genetec Security Center (VMS)', description: 'Cameras via Security Center SDK.' },
    ],
  },
  {
    category: 'Access Control',
    Icon: Shield,
    color: '#10b981',
    items: [
      { type: 'FEENICS_ACCESS', name: 'Feenics (Acre)',                description: 'Cloud-based access control by Acre.' },
      { type: 'GENETEC_ACS',    name: 'Genetec Security Center (ACS)', description: 'Synergis access control.' },
      { type: 'LENEL_ONGUARD',  name: 'Lenel OnGuard',                 description: 'Coming soon.', comingSoon: true },
      { type: 'CCURE_9000',     name: 'CCure 9000',                    description: 'Coming soon.', comingSoon: true },
    ],
  },
  {
    category: 'Intercoms',
    Icon: Phone,
    color: '#f59e0b',
    items: [
      { type: 'INTERCOM_2N',     name: '2N Intercoms',     description: 'SIP intercoms by 2N.' },
      { type: 'INTERCOM_HANWHA', name: 'Hanwha Intercoms', description: 'Hanwha SIP intercoms.' },
    ],
  },
  {
    category: 'AI & Analytics',
    Icon: Brain,
    color: '#a78bfa',
    items: [
      { type: 'OOSTO_FR', name: 'Oosto Facial Recognition', description: 'Real-time facial recognition analytics.' },
    ],
  },
];

const IntegrationSelectStep = ({ onNext }) => {
  const { savedData, markStepComplete } = useSiteSetupStore();
  const { branding } = useBranding();
  const brandName = branding.brandName;
  const initial = new Set(savedData?.selectedIntegrations || []);
  const [selected, setSelected] = useState(initial);
  const [confirmSkip, setConfirmSkip] = useState(false);

  const toggle = (type, disabled) => {
    if (disabled) return;
    const next = new Set(selected);
    if (next.has(type)) next.delete(type); else next.add(type);
    setSelected(next);
    setConfirmSkip(false);
  };

  const handleNext = async () => {
    if (selected.size === 0 && !confirmSkip) {
      setConfirmSkip(true);
      return;
    }
    await markStepComplete('integrations', {
      selectedIntegrations: Array.from(selected),
    });
    onNext();
  };

  return (
    <div className="site-setup-step integration-select-step">
      <h3>Connect Integrations</h3>
      <p className="step-description">
        Select the systems you'd like to connect to {brandName}. You can add more later from Settings.
      </p>

      {CATALOG.map(cat => {
        const Icon = cat.Icon;
        return (
          <div key={cat.category} className="integration-category">
            <div className="integration-category-header">
              <Icon size={16} style={{ color: cat.color }} />
              <span>{cat.category}</span>
            </div>
            <div className="integration-card-grid">
              {cat.items.map(item => {
                const isSelected = selected.has(item.type);
                const isDisabled = !!item.comingSoon;
                return (
                  <button
                    key={item.type}
                    type="button"
                    className={
                      'integration-card' +
                      (isSelected ? ' selected' : '') +
                      (isDisabled ? ' disabled' : '')
                    }
                    onClick={() => toggle(item.type, isDisabled)}
                    disabled={isDisabled}
                  >
                    <div className="integration-card-check">
                      {isSelected && <Check size={14} />}
                    </div>
                    <div className="integration-card-body">
                      <strong>{item.name}</strong>
                      <span>{item.description}</span>
                    </div>
                    {item.comingSoon && (
                      <span className="integration-coming-soon">Coming soon</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}

      {confirmSkip && (
        <div className="setup-hint-row" style={{ marginTop: 16, color: '#fbbf24' }}>
          <Info size={14} />
          <span>No integrations selected. Click Next again to skip, or pick one above. You can set these up later from Settings.</span>
        </div>
      )}

      <div className="step-actions">
        <button className="site-setup-btn ghost" onClick={handleNext}>
          <SkipForward size={14} /> Skip for Now
        </button>
        <button className="site-setup-btn primary" onClick={handleNext}>
          {selected.size > 0
            ? `Configure ${selected.size} Integration${selected.size === 1 ? '' : 's'}`
            : 'Continue'}
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default IntegrationSelectStep;
